<script setup lang="ts">
import { computed, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import DatePicker from "primevue/datepicker";
import TooltipButton from "../../common/TooltipButton.vue";
import { DirectLink, Project, Trainer } from "../../../types/PersologOnlineTypes.ts";
import { useProjectDetailsDirectLinkStore } from "../../../stores/projectDetailsDirectLink";
import { copyToClipboard, openInNewTab } from "../../../utils/GeneralUtils.ts";
import { parseServerDateOnly, toServerEndOfDay } from "@persolog/utils";

const { t } = useI18n();

/**
 * Manages the optional short link ("tiny URL") of a direct-link project, shown inside the QR dialog.
 * The short link is meant for live sessions/talks; it can be created with an optional custom name and
 * expiry, renamed (create-new-then-drop-old) or deleted. The normal long direct link is shown elsewhere.
 *
 * Everything is inline (view / edit / confirm-delete sections) — no nested overlays — so it works
 * reliably inside the surrounding modal dialog.
 */
const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        default: null,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    /** The exact long direct-link URL to shorten (built by the parent from the same env config as the displayed link). */
    targetUrl: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(["update:project"]);

const store = useProjectDetailsDirectLinkStore();

const SLUG_PATTERN = /^[a-z0-9-]{3,64}$/;
type Mode = "view" | "edit" | "confirmDelete";
const mode = ref<Mode>("view");
const slug = ref("");
const expiry = ref<Date | null>(null);
const submitting = ref(false);
const errorMessage = ref("");
const today = new Date();

const hasShortLink = computed(() => !!props.directLink?.shortUrl);
const slugTrimmed = computed(() => slug.value.trim());
const slugValid = computed(() => slugTrimmed.value === "" || SLUG_PATTERN.test(slugTrimmed.value));
const expiresOn = computed(() =>
    props.directLink?.shortLinkExpiresAt
        ? parseServerDateOnly(props.directLink.shortLinkExpiresAt).toLocaleDateString()
        : null,
);

function openEdit(prefill: boolean) {
    // On rename, prefill the current slug (parsed from the short URL) and expiry for convenience.
    slug.value = prefill && props.directLink?.shortUrl ? props.directLink.shortUrl.split("/").pop() ?? "" : "";
    expiry.value = prefill && props.directLink?.shortLinkExpiresAt
        ? parseServerDateOnly(props.directLink.shortLinkExpiresAt)
        : null;
    errorMessage.value = "";
    mode.value = "edit";
}

function cancel() {
    mode.value = "view";
    errorMessage.value = "";
}

async function save() {
    if (!slugValid.value || props.project.id === undefined) return;
    submitting.value = true;
    errorMessage.value = "";
    try {
        const updated = await store.createShortLink(props.project, props.trainer.id, {
            targetUrl: props.targetUrl,
            customSlug: slugTrimmed.value || undefined,
            expiresAt: expiry.value ? toServerEndOfDay(expiry.value) : undefined,
        });
        emit("update:project", updated);
        mode.value = "view";
    } catch (error) {
        console.error("Failed to create short link:", error);
        errorMessage.value = t("project-configuration.directLink.shortLinkError");
    } finally {
        submitting.value = false;
    }
}

async function remove() {
    if (props.project.id === undefined) return;
    submitting.value = true;
    try {
        const updated = await store.deleteShortLink(props.project, props.trainer.id);
        emit("update:project", updated);
        mode.value = "view";
    } finally {
        submitting.value = false;
    }
}
</script>

<template>
    <div class="short-link flex flex-col gap-2 border-t border-border-ui pt-4">
        <div class="font-bold text-sm">{{ t("project-configuration.directLink.shortLinkTitle") }}</div>
        <small class="text-content-muted">{{ t("project-configuration.directLink.shortLinkExplanation") }}</small>

        <!-- View: existing short link -->
        <template v-if="mode === 'view' && hasShortLink">
            <div class="flex items-center gap-1">
                <InputText
                    id="short-link_input_url"
                    :value="directLink?.shortUrl ?? ''"
                    class="flex-1 text-sm"
                    readonly
                />
                <Button
                    id="short-link_btn_copy"
                    severity="secondary"
                    icon="pi pi-copy"
                    class="bg-persolog-blue text-white"
                    @click="copyToClipboard(directLink?.shortUrl ?? '')"
                    v-tooltip.top="{ value: t('project-details.direct-link.copy'), showDelay: 500 }"
                />
                <Button
                    id="short-link_btn_open"
                    severity="secondary"
                    icon="pi pi-external-link"
                    class="bg-persolog-blue text-white"
                    @click="openInNewTab(directLink?.shortUrl ?? '')"
                    v-tooltip.top="{ value: t('project-details.direct-link.open'), showDelay: 500 }"
                />
            </div>
            <small class="text-content-muted">
                {{ expiresOn
                    ? t("project-configuration.directLink.shortLinkExpiresOn", { date: expiresOn })
                    : t("project-configuration.directLink.shortLinkNoExpiry") }}
            </small>
            <div class="flex gap-2 mt-1">
                <Button
                    id="short-link_btn_rename"
                    size="small"
                    outlined
                    icon="pi pi-pencil"
                    :label="t('project-configuration.directLink.shortLinkRename')"
                    @click="openEdit(true)"
                />
                <Button
                    id="short-link_btn_delete"
                    size="small"
                    outlined
                    severity="danger"
                    icon="pi pi-trash"
                    :label="t('project-configuration.directLink.shortLinkDelete')"
                    @click="mode = 'confirmDelete'"
                />
            </div>
        </template>

        <!-- View: no short link yet -->
        <Button
            v-else-if="mode === 'view'"
            id="short-link_btn_create"
            size="small"
            icon="pi pi-link"
            :label="t('project-configuration.directLink.shortLinkCreate')"
            @click="openEdit(false)"
        />

        <!-- Edit / create -->
        <template v-else-if="mode === 'edit'">
            <label for="short-link_input_slug" class="text-sm font-bold">
                {{ t("project-configuration.directLink.customSlugLabel") }}
            </label>
            <InputText
                id="short-link_input_slug"
                v-model="slug"
                :invalid="!slugValid"
                placeholder="consulting-talk"
                autocomplete="off"
            />
            <small v-if="!slugValid" class="text-red-600">
                {{ t("project-configuration.directLink.customSlugInvalid") }}
            </small>
            <small v-else class="text-content-muted">
                {{ t("project-configuration.directLink.customSlugHint") }}
            </small>

            <div class="flex items-center mt-2">
                <label for="short-link_input_expiry" class="text-sm font-bold">
                    {{ t("project-configuration.directLink.shortLinkExpiryLabel") }}
                </label>
                <TooltipButton
                    id="short-link_btn_expiry-info"
                    icon="pi pi-info-circle"
                    severity="secondary"
                    text
                    rounded
                    class="p-0 ml-1 w-8 h-8"
                    :content="t('project-configuration.directLink.shortLinkExpiryTooltip')"
                    :aria-label="t('accessibility.buttons.info')"
                />
            </div>
            <DatePicker
                id="short-link_input_expiry"
                v-model="expiry"
                :min-date="today"
                show-icon
                show-button-bar
                :date-format="t('formats.datePicker')"
                :placeholder="t('project-configuration.directLink.shortLinkNoExpiry')"
            />

            <small v-if="errorMessage" class="text-red-600 mt-1">{{ errorMessage }}</small>

            <div class="flex justify-end gap-2 mt-2">
                <Button id="short-link_btn_cancel" size="small" text severity="secondary" :label="t('buttons.cancel')" @click="cancel" />
                <Button
                    id="short-link_btn_save"
                    size="small"
                    :label="t('buttons.ok')"
                    :disabled="!slugValid || submitting"
                    :loading="submitting"
                    @click="save"
                />
            </div>
        </template>

        <!-- Confirm delete -->
        <template v-else>
            <p class="text-sm">{{ t("project-configuration.directLink.shortLinkDeleteConfirm") }}</p>
            <div class="flex justify-end gap-2">
                <Button size="small" text severity="secondary" :label="t('buttons.cancel')" @click="cancel" />
                <Button
                    id="short-link_btn_confirm-delete"
                    size="small"
                    severity="danger"
                    :label="t('project-configuration.directLink.shortLinkDelete')"
                    :loading="submitting"
                    @click="remove"
                />
            </div>
        </template>
    </div>
</template>
